import React, { memo, useContext, useEffect, useMemo, useState } from 'react';
import { Blok } from '../../interfaces/Blok.interface';
import { StoryblokAsset } from '../../interfaces/StoryblokAsset.interface';
import { Theme } from '../../interfaces/enums/Theme.enum';
import { withBlokWrapper } from '../BlokWrapper/BlokWrapper';
import Heading, { HeadingTypes } from '../Heading/Heading';
import SbImage from '../SbImage/SbImage';
import Col from '../Utils/Col/Col';
import Container from '../Utils/Container/Container';
import Grid from '../Utils/Grid/Grid';
import {
  Configure,
  InstantSearch,
  InstantSearchSSRProvider,
} from 'react-instantsearch-hooks-web';
import { AuthorContext } from '../../contexts/AuthorContext';
import algoliasearch from 'algoliasearch';
import { getQueryParam } from '../../utils/getQueryParam/getQueryParam.util';
import ArticlesPublished from './ArticlesPublished';
import RichText from '../RichText/RichText';
import { StoryblokRichtext } from 'storyblok-rich-text-react-renderer';

interface AuthorHeroBlokProps extends Blok {
  image: StoryblokAsset;
  arrow_image?: StoryblokAsset;
  heading: string;
  title?: string;
  body?: StoryblokRichtext;
  articles_published: number;
}

interface AuthorHeroProps {
  blok: AuthorHeroBlokProps;
}

const AuthorHero: React.FC<AuthorHeroProps> = ({ blok }) => {
  const { image, arrow_image, heading, title, body, articles_published } = blok;

  const { author } = useContext(AuthorContext);

  const indexName: string = useMemo(() => {
    return `${process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME}`;
  }, [process.env.GATSBY_ALGOLIA_SITE_SEARCH_INDEX_NAME]);

  const algoliaSearchClient = useMemo(() => {
    return algoliasearch(
      `${process.env.GATSBY_ALGOLIA_APP_ID}`,
      `${process.env.GATSBY_ALGOLIA_SEARCH_API_KEY}`
    );
  }, [
    process.env.GATSBY_ALGOLIA_APP_ID,
    process.env.GATSBY_ALGOLIA_SEARCH_API_KEY,
  ]);

  const [isLoading, setIsLoading] = useState<boolean>(true);

  const baseFilters: string = 'field_component:article';
  const authors = `authors:"${author}"`;
  const filters = `${baseFilters} AND ${authors}`;

  const getInitialQuery = (): string => {
    if (typeof window === 'undefined') return '';
    return getQueryParam('q') || '';
  };

  const getInitialPage = (): number => {
    if (typeof window === 'undefined') return 0;
    return parseInt(getQueryParam('page')!) || 0;
  };

  /*-- useEffects --*/
  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 400);
  }, []);

  return (
    <InstantSearchSSRProvider>
      <InstantSearch
        indexName={indexName}
        searchClient={algoliaSearchClient}
        initialUiState={{
          [indexName]: {
            query: getInitialQuery(),
            page: getInitialPage(),
          },
        }}
      >
        <Configure
          filters={filters}
          // hitsPerPage={number_of_results_to_display || 9}
        />
        <section className="relative overflow-hidden lg:h-[620px] 2xl:h-[680px]">
          <div className="relative h-full">
            <Container className="h-full">
              <Grid gapX="gap-x-[24px]" gapY="gap-y-0" className="h-full">
                <Col
                  span={{
                    default: 12,
                    sm: 12,
                    md: 12,
                    lg: 8,
                    xl: 8,
                    xxl: 8,
                  }}
                >
                  <div className="flex flex-col items-center justify-center gap-y-12 lg:items-start pb-10 pt-16 sm:pb-12 sm:pt-20 lg:pb-0 lg:pt-32 lg:pr-[56px] xl:pr-[154px]">
                    <div className="flex flex-col items-center justify-center text-center lg:items-start lg:text-left">
                      {heading && (
                        <Heading
                          text={heading}
                          headingType={HeadingTypes.h1}
                          className="!text-[48px] md:!text-[56px]"
                        />
                      )}
                      {title && (
                        <p className="text-2xl text-text/body-primary font-dm-serif-display pt-2">
                          {title}
                        </p>
                      )}
                      {articles_published && <ArticlesPublished />}
                      {body && <RichText content={body} />}
                    </div>
                  </div>
                </Col>
                <Col
                  span={{
                    default: 12,
                    sm: 12,
                    md: 12,
                    lg: 4,
                    xl: 4,
                    xxl: 4,
                  }}
                >
                  {image?.filename && (
                    <div className="relative h-full flex items-end lg:pt-32">
                      <SbImage
                        asset={image}
                        options={{
                          objectFit: 'cover',
                          objectPosition: 'top',
                          className: 'max-h-[450px] lg:max-h-none h-full',
                        }}
                      />
                    </div>
                  )}
                </Col>

                <div className="absolute right-0 bottom-0 left-0 overflow-hidden pointer-events-none">
                  {arrow_image?.filename && (
                    <div className="hidden lg:flex items-end lg:h-full lg:pt-16 lg:-translate-x-8 2xl:-translate-x-12">
                      <SbImage
                        asset={arrow_image}
                        options={{
                          className:
                            '!h-[auto] lg:max-w-[42em] xl:max-w-[55em] 2xl:max-w-[60em]',
                        }}
                      />
                    </div>
                  )}
                </div>
              </Grid>
            </Container>
          </div>
        </section>
      </InstantSearch>
    </InstantSearchSSRProvider>
  );
};

export default memo(withBlokWrapper(AuthorHero, Theme.DARK));
